import React from "react";
import './ThreeDText.css';

let __elakayInfoSource;
const __setElakayInfoSource = (infoSource) => { if (infoSource) __elakayInfoSource = infoSource; };
const __elakayText = (key, fallback) =>
  __elakayInfoSource?.laguangeElakay?.("frontElakayHardcodedText", __elakayInfoSource.languageAPI)?.["threeDtext"]?.[key] || fallback;

// ⬅️ nouveau import global
/**
 * ThreeDText.jsx — combined (+ placement presets + pos aliases + align + .; line breaks)
 * - 3D/effect variants + 20 decorative font variants
 * - Combo syntax: "base:font" (e.g., "chromeBevel:playfairDisplay")
 * - Placement presets: center, topCenter, bottomCenter, centerRight, centerLeft
 * - Shorthand alias prop: pos = 'c' | 'tc' | 'bc' | 'cr' | 'cl'
 * - Text alignment: left | center | right via `textAlign`
 * - Custom line breaks: split on token ".;" to create new lines
 * - Responsive size: number | string | {min,max} | {sm,md,lg}
 * - Overlay-friendly: position + zIndex + reflection
 * - Color overrides via CSS variables through `colors` prop
 */

/* ==========================
 * Sizing helpers
 * ========================== */
const matriceTreeDText = {
  "component": "ThreeDText",
  "version": "1.0",
  "description": __elakayText("t001", "Texte décoratif avec effets 3D, polices décoratives, placements, alignements, tailles responsives, réflexion et règles de combinaison base:font."),
  "lineBreakToken": ".;",
  "props": [
    {
      "name": __elakayText("t002", "text"),
      "type": "string | ReactNode",
      "default": "Lakay Yvox",
      "description": __elakayText("t003", "Contenu du texte. Si string, l’accessibilité utilise une aria-label nettoyée (token .; retiré). Le token '.;' force un saut de ligne.")
    },
    {
      "name": __elakayText("t004", "variant"),
      "type": "string",
      "default": "grey3D",
      "description": __elakayText("t005", "Nom de la variante. Accepte un effet (base), une police (font) ou une combinaison 'base:font'. Voir 'variants'.")
    },
    {
      "name": __elakayText("t006", "size"),
      "type": "number | string | {min,max} | {sm,md,lg}",
      "default": 140,
      "description": __elakayText("t007", "Taille du texte. number => px, string => CSS (ex: '10vw'), {min,max} => clamp responsive, {sm,md,lg} => breakpoints.")
    },
    {
      "name": __elakayText("t008", "colors"),
      "type": "{ text?, stroke?, outline?, shadow?, reflection?, gradientStart?, gradientEnd?, glow? }",
      "default": "{}",
      "description": __elakayText("t009", "Override fin des couleurs et matières. Voir 'colorKeys' pour les rôles.")
    },
    {
      "name": __elakayText("t010", "reflect"),
      "type": "boolean",
      "default": true,
      "description": __elakayText("t011", "Affiche une réflexion/miroir sous le texte lorsque supporté par la variante.")
    },
    {
      "name": __elakayText("t012", "className"),
      "type": "string",
      "default": "",
      "description": __elakayText("t013", "Classes CSS additionnelles sur le wrapper.")
    },
    {
      "name": __elakayText("t014", "style"),
      "type": "object",
      "default": "{}",
      "description": __elakayText("t015", "Styles inline additionnels sur le wrapper.")
    },
    {
      "name": __elakayText("t016", "position"),
      "type": "{ position?, top?, right?, bottom?, left? }",
      "default": "null",
      "description": __elakayText("t017", "Mode placement libre (hérité). Utiliser de préférence 'placement' / 'pos' pour des positions rapides.")
    },
    {
      "name": __elakayText("t018", "zIndex"),
      "type": "number",
      "default": 9999,
      "description": __elakayText("t019", "z-index du wrapper pour superpositions.")
    },
    {
      "name": __elakayText("t020", "placement"),
      "type": "'inline' | 'center' | 'topCenter' | 'bottomCenter' | 'centerRight' | 'centerLeft'",
      "default": "inline",
      "description": __elakayText("t021", "Préréglage de position absolue. Le parent doit être positionné en 'relative'.")
    },
    {
      "name": __elakayText("t022", "offset"),
      "type": "{ x?: number|string, y?: number|string }",
      "default": "{ x: 0, y: 0 }",
      "description": __elakayText("t023", "Décalage fin appliqué au 'placement' (px ou CSS ex: '1rem', '10%').")
    },
    {
      "name": __elakayText("t024", "pos"),
      "type": "'c' | 'tc' | 'bc' | 'cr' | 'cl'",
      "default": "null",
      "description": __elakayText("t025", "Alias rapides pour placers ('c' center, 'tc' topCenter, 'bc' bottomCenter, 'cr' centerRight, 'cl' centerLeft). Si défini, supplante 'placement' et 'offset'.")
    },
    {
      "name": __elakayText("t026", "textAlign"),
      "type": "'left' | 'center' | 'right'",
      "default": "center",
      "description": __elakayText("t027", "Alignement interne du texte (affecte toutes les lignes créées par '.;').")
    }
  ],
  "variants": {
    "baseEffects": [
      {
        "name": __elakayText("t028", "grey3D"),
        "purpose": "Métal/relief gris sobre",
        "colorKeys": ["gradientStart", "gradientEnd", "shadow", "reflection"]
      },
      {
        "name": __elakayText("t029", "orange3D"),
        "purpose": "Ombre volumique orange (sticker)",
        "colorKeys": ["text", "shadow", "reflection"]
      },
      {
        "name": __elakayText("t030", "yellowOutline"),
        "purpose": "Script lumineux avec contour",
        "colorKeys": ["text", "stroke", "shadow"],
        "notes": "Reflection désactivée par style"
      },
      {
        "name": __elakayText("t031", "redCartoon"),
        "purpose": "Cartoon avec gros outline",
        "colorKeys": ["text", "outline", "shadow", "reflection"]
      },
      {
        "name": __elakayText("t032", "neonGlow"),
        "purpose": "Enseigne néon (halo)",
        "colorKeys": ["text", "glow"]
      },
      {
        "name": __elakayText("t033", "chromeBevel"),
        "purpose": "Chrome/Bevel premium",
        "colorKeys": ["gradientStart", "gradientEnd", "shadow", "reflection"]
      },
      {
        "name": __elakayText("t034", "bubbleGum"),
        "purpose": "Pastel ludique à gros outline",
        "colorKeys": ["text", "outline", "reflection"]
      },
      {
        "name": __elakayText("t035", "gradientOutline"),
        "purpose": "Dégradé + outline net",
        "colorKeys": ["gradientStart", "gradientEnd", "outline"]
      },
      {
        "name": __elakayText("t036", "halftoneComic"),
        "purpose": "Trame BD pop",
        "colorKeys": ["text", "outline"],
        "notes": "Reflection masquée par style"
      },
      {
        "name": __elakayText("t037", "glassy"),
        "purpose": "Verre/gel translucide",
        "colorKeys": ["gradientStart", "gradientEnd", "stroke", "reflection"]
      }
    ],
    "decorativeFonts": [
      "playfairDisplay",
      "greatVibes",
      "pacifico",
      "lobster",
      "cinzel",
      "unifrakturCook",
      "abrilFatface",
      "montez",
      "sacramento",
      "rougeScript",
      "bebasNeue",
      "prata",
      "dancingScript",
      "parisienne",
      "cormorantGaramond",
      "oswald",
      "quicksand",
      "poiretOne",
      "medievalSharp",
      "birthstone"
    ],
    "combinationRule": {
      "syntax": "BASE:FONT",
      "description": __elakayText("t038", "Combine un effet (base) et une police décorative. Exemple: 'chromeBevel:playfairDisplay'."),
      "examples": [
        "chromeBevel:playfairDisplay",
        "neonGlow:oswald",
        "gradientOutline:bebasNeue",
        "redCartoon:unifrakturCook",
        "glassy:prata"
      ]
    }
  },
  "colorKeys": [
    { "key": "text", "usage": "Couleur de remplissage (quand pas en dégradé) et néon" },
    { "key": "stroke", "usage": "Fin contour (ex: glassy)" },
    { "key": "outline", "usage": "Gros contour (ex: redCartoon, bubbleGum)" },
    { "key": "shadow", "usage": "Ombres/relief et faux volume" },
    { "key": "reflection", "usage": "Teinte de la réflexion miroir" },
    { "key": "gradientStart", "usage": "Début du dégradé (grey3D, gradientOutline, glassy, chromeBevel)" },
    { "key": "gradientEnd", "usage": "Fin du dégradé" },
    { "key": "glow", "usage": "Halo du néon (neonGlow)" }
    ],
  "placement": {
    "presets": [
      "inline",
      "center",
      "topCenter",
      "bottomCenter",
      "centerRight",
      "centerLeft"
    ],
    "aliases": {
      "c": "center",
      "tc": "topCenter",
      "bc": "bottomCenter",
      "cr": "centerRight",
      "cl": "centerLeft"
    },
    "requirements": "Le parent direct doit avoir position: relative pour les placements (hors 'inline').",
    "offset": "Décalage fin {x, y} en px ou unité CSS (ex: '1rem', '10%')."
  },
  "sizing": {
    "modes": [
      { "type": "number", "example": 120, "note": "px" },
      { "type": "string", "example": "10vw", "note": "CSS brut (vw, rem, etc.)" },
      { "type": "{min,max}", "example": { "min": 56, "max": 180 }, "note": "clamp responsive" },
      { "type": "{sm,md,lg}", "example": { "sm": 32, "md": 64, "lg": 128 }, "note": "Breakpoints" }
    ],
    "recommendations": [
      "Héros: { min: 56, max: 180 } ou { sm:48, md:96, lg:160 }",
      "Badges: { sm:32, md:56, lg:96 }",
      "Mobile net: sm ≈ 28–44"
    ]
  },
  "text": {
    "alignment": ["left", "center", "right"],
    "lineBreakToken": ".;",
    "lineBreakUsage": "Chaque '.;' dans 'text' crée une nouvelle ligne (le token est supprimé)."
  },
  "accessibility": {
    "role": "img",
    "ariaLabel": "Générée depuis 'text' (token .; retiré). Mettre text='' si purement décoratif."
  },
  "usageExamples": [
    {
      "title": __elakayText("t039", "Base effet métal"),
      "code": "<ThreeDText text=\"METAL\" variant=\"grey3D\" size={{ min:48, max:160 }} />"
    },
    {
      "title": __elakayText("t040", "Néon"),
      "code": "<ThreeDText text=\"OPEN\" variant=\"neonGlow\" colors={{ text:'#39ff14', glow:'#39ff14' }} size={{ sm:36, md:72, lg:120 }} />"
    },
    {
      "title": __elakayText("t041", "Police seule"),
      "code": "<ThreeDText text=\"Ilakay\" variant=\"playfairDisplay\" />"
    },
    {
      "title": __elakayText("t042", "Combo chrome + Playfair"),
      "code": "<ThreeDText text=\"ELAKAY\" variant=\"chromeBevel:playfairDisplay\" size={{ min:56, max:180 }} reflect={false} />"
    },
    {
      "title": __elakayText("t043", "Dégradé + outline"),
      "code": "<ThreeDText text=\"GRADIENT\" variant=\"gradientOutline:bebasNeue\" colors={{ gradientStart:'#ff8a00', gradientEnd:'#e52e71', outline:'#141414' }} />"
    },
    {
      "title": __elakayText("t044", "Placement center"),
      "code": "<div className=\"relative h-[300px]\"><ThreeDText text=\"Center\" variant=\"chromeBevel\" placement=\"center\" /></div>"
    },
    {
      "title": __elakayText("t045", "Alias pos topCenter"),
      "code": "<ThreeDText text=\"Top Center\" variant=\"neonGlow\" pos=\"tc\" />"
    },
    {
      "title": __elakayText("t046", "Alignement + sauts de ligne"),
      "code": "<ThreeDText text={\"WELCOME.;TO.;ELAKAY\"} textAlign=\"right\" variant=\"glassy:prata\" />"
    }
  ],
  "integrationNotes": [
    "Les Google Fonts sont importées en CSS via @import dans le composant. Si votre build l’interdit, déplacer l’URL d’import dans le CSS global.",
    "Pour overlay, toujours mettre le parent en position: relative.",
    "Limiter la reflection sur fonds chargés (reflect=false) si la lisibilité baisse."
  ],
  "presetsSuggestion": [
    {
      "name": __elakayText("t047", "HeroChromePlayfair"),
      "variant": "chromeBevel:playfairDisplay",
      "size": { "min": 56, "max": 180 },
      "colors": {},
      "reflect": false
    },
    {
      "name": __elakayText("t048", "NeonOswaldHeadline"),
      "variant": "neonGlow:oswald",
      "size": { "sm": 40, "md": 72, "lg": 120 },
      "colors": { "text": "#39ff14", "glow": "#39ff14" },
      "reflect": true
    },
    {
      "name": __elakayText("t049", "GradientBebasPromo"),
      "variant": "gradientOutline:bebasNeue",
      "size": { "sm": 44, "md": 80, "lg": 140 },
      "colors": { "gradientStart": "#ff8a00", "gradientEnd": "#e52e71", "outline": "#141414" }
    }
  ]
}


function px(v) { return (typeof v === 'number' ? `${v}px` : v); }

function resolveSizeVars(size) {
  if (typeof size === 'number' || typeof size === 'string') {
    return { base: px(size), useBreakpoints: false, useClamp: false };
  }
  if (size && typeof size === 'object') {
    if ('min' in size || 'max' in size) {
      const min = px(size.min ?? 32);
      const max = px(size.max ?? 140);
      return { base: `clamp(${min}, 6vw, ${max})`, useBreakpoints: false, useClamp: true, min, max };
    }
    if ('sm' in size || 'md' in size || 'lg' in size) {
      return {
        base: px(size.md ?? size.lg ?? size.sm ?? 96),
        sm: px(size.sm ?? size.md ?? size.lg ?? 72),
        md: px(size.md ?? size.lg ?? size.sm ?? 96),
        lg: px(size.lg ?? size.md ?? size.sm ?? 140),
        useBreakpoints: true,
        useClamp: false,
      };
    }
  }
  return { base: '140px', useBreakpoints: false, useClamp: false };
}

/* ==========================
 * Placement helpers
 * ========================== */
function toCssPx(v) { return typeof v === 'number' ? `${v}px` : (v ?? '0'); }

function resolvePlacement(placement, offset = {}) {
  if (!placement || placement === 'inline') return {};

  const x = typeof offset.x === 'number' ? `${offset.x}px` : offset.x || '0';
  const y = typeof offset.y === 'number' ? `${offset.y}px` : offset.y || '0';
  const base = { position: 'absolute' };

  switch (placement) {
    case 'topLeft':
      return { ...base, top: 0, left: 0, transform: `translate(${x}, ${y})` };
    case 'topCenter':
      return { ...base, top: 0, left: '50%', transform: `translate(-50%, ${y})` };
    case 'topRight':
      return { ...base, top: 0, right: 0, transform: `translate(${x}, ${y})` };
    case 'centerLeft':
      return { ...base, top: '50%', left: 0, transform: `translate(${x}, -50%)` };
    case 'center':
      return { ...base, top: '50%', left: '50%', transform: `translate(-50%, -50%) translate(${x}, ${y})` };
    case 'centerRight':
      return { ...base, top: '50%', right: 0, transform: `translate(${x}, -50%)` };
    case 'bottomLeft':
      return { ...base, bottom: 0, left: 0, transform: `translate(${x}, ${y})` };
    case 'bottomCenter':
      return { ...base, bottom: 0, left: '50%', transform: `translate(-50%, ${y})` };
    case 'bottomRight':
      return { ...base, bottom: 0, right: 0, transform: `translate(${x}, ${y})` };
    default:
      return {};
  }
}


// Shorthand alias parser (pos): 'c'|'tc'|'bc'|'cr'|'cl'

function parsePos(pos) {
  if (!pos) return { placement: 'inline', offset: { x: 0, y: 0 } };
  const norm = String(pos).trim().toLowerCase();

  switch (norm) {
    // Top positions
    case 'tl': return { placement: 'topLeft',      offset: { x: 0, y: 0 } };
    case 'tc': return { placement: 'topCenter',    offset: { x: 0, y: 0 } };
    case 'tr': return { placement: 'topRight',     offset: { x: 0, y: 0 } };

    // Center (vertical middle)
    case 'cl': return { placement: 'centerLeft',   offset: { x: 0, y: 0 } };
    case 'c':  return { placement: 'center',       offset: { x: 0, y: 0 } };
    case 'cr': return { placement: 'centerRight',  offset: { x: 0, y: 0 } };

    // Bottom positions
    case 'bl': return { placement: 'bottomLeft',   offset: { x: 0, y: 0 } };
    case 'bc': return { placement: 'bottomCenter', offset: { x: 0, y: 0 } };
    case 'br': return { placement: 'bottomRight',  offset: { x: 0, y: 0 } };

    default:   return { placement: 'inline',       offset: { x: 0, y: 0 } };
  }
}


/* ==========================
 * Text helpers
 * ========================== */
function resolveTextAlign(align) {
  const v = (align || '').toString().toLowerCase();
  return v === 'left' || v === 'right' || v === 'center' ? v : 'center';
}

function renderWithLineBreaks(content) {
  if (content == null) return '';
  if (typeof content !== 'string') return content; // Allow ReactNode passthrough
  // Split on token ".;" and create block lines; token is removed
  const parts = content.split('.;');
  return parts?.map((seg, idx) => (
    <span className="td-line" key={idx}>{seg}</span>
  ));
}

const clampNum = (v, min, max) => Math.min(Math.max(v, min), max);
const countLines = (text) => {
  if (Array.isArray(text)) return text?.length;
  if (typeof text !== 'string') return 1;
  return (text.split(/\n/g)?.length) || 1;
};

/* ==========================
 * Effect presets (materials)
 * ========================== */
const PRESET_COLORS = {
  grey3D: { text: '#000000', gradientStart: '#ffffff', gradientEnd: '#cfcfcf', shadow: 'rgba(0,0,0,0.45)', reflection: 'rgba(0,0,0,0.6)' },
  orange3D: { text: '#ffffff', shadow: '#ffb400', reflection: 'rgba(0,0,0,0.45)' },
  yellowOutline: { text: '#ffd447', stroke: 'rgba(0,0,0,0.25)', shadow: '#163b2f' },
  redCartoon: { text: '#ffffff', outline: '#d32222', shadow: '#c11c1c', reflection: 'rgba(0,0,0,0.5)' },
  neonGlow: { text: '#39ff14', glow: '#39ff14', shadow: 'rgba(0,0,0,0.4)' },
  chromeBevel: { text: '#e8f0ff', gradientStart: '#f5f7fa', gradientEnd: '#b5c1d1', shadow: 'rgba(0,0,0,0.5)', reflection: 'rgba(255,255,255,0.7)' },
  bubbleGum: { text: '#ffe6ef', outline: '#ff6ea8', shadow: 'rgba(255,110,168,0.65)', reflection: 'rgba(255,182,193,0.6)' },
  gradientOutline: { text: '#ffffff', outline: '#141414', gradientStart: '#ff8a00', gradientEnd: '#e52e71', shadow: 'rgba(0,0,0,0.35)' },
  halftoneComic: { text: '#fff', outline: '#111', shadow: 'rgba(0,0,0,0.45)' },
  glassy: { text: '#ffffff', stroke: 'rgba(255,255,255,0.35)', gradientStart: 'rgba(255,255,255,0.85)', gradientEnd: 'rgba(255,255,255,0.2)', shadow: 'rgba(0,0,0,0.25)', reflection: 'rgba(255,255,255,0.75)' }
};

/* ==========================
 * Decorative typefaces (20)
 * ========================== */
const TYPEFACE_MAP = {
  playfairDisplay: "'Playfair Display', serif",
  greatVibes: "'Great Vibes', cursive",
  pacifico: "'Pacifico', cursive",
  lobster: "'Lobster', cursive",
  cinzel: "'Cinzel', serif",
  unifrakturCook: "'UnifrakturCook', cursive",
  abrilFatface: "'Abril Fatface', serif",
  montez: "'Montez', cursive",
  sacramento: "'Sacramento', cursive",
  rougeScript: "'Rouge Script', cursive",
  bebasNeue: "'Bebas Neue', sans-serif",
  prata: "'Prata', serif",
  dancingScript: "'Dancing Script', cursive",
  parisienne: "'Parisienne', cursive",
  cormorantGaramond: "'Cormorant Garamond', serif",
  oswald: "'Oswald', sans-serif",
  quicksand: "'Quicksand', sans-serif",
  poiretOne: "'Poiret One', cursive",
  medievalSharp: "'MedievalSharp', cursive",
  birthstone: "'Birthstone', cursive",
};

/* ==========================
 * Proportional metrics + contrast helpers
 * ========================== */
function computeTextMetrics(size, lines = 1, tune = {}) {
  const density  = tune.density ?? 1;
  const strokeK  = tune.strokeK ?? 0.028;
  const outlineK = tune.outlineK ?? 0.048;
  const glowK    = tune.glowK ?? 0.18;
  const shadowK  = tune.shadowK ?? 0.08;
  const blurK    = tune.blurK ?? 0.12;
  const lineGapK = tune.lineGapK ?? 0.24;
  const letterK  = tune.letterK ?? -0.02;

  const S = parseFloat(size) || 140;

  const strokeW    = clampNum(S * strokeK  * density, 0.5, 10);
  const outlineW   = clampNum(S * outlineK * density, 1, 18);
  const shadowOff  = clampNum(S * shadowK  * density, 1, 24);
  const shadowBlur = clampNum(S * blurK    * density, 2, 40);
  const glowRadius = clampNum(S * glowK    * density, 4, 80);

  const lineHeight = lines > 1 ? clampNum(1 + 0.12 * density, 1.05, 1.3) : 1.0;
  const lineGapPx  = clampNum(S * lineGapK * (lines > 1 ? 1 : 0) * density, 0, S * 0.35);

  const reflectShift = clampNum(S * 0.05, 2, 24);
  const reflectBlur  = clampNum(S * 0.08, 2, 36);
  const letterSp     = S * letterK;

  return { strokeW, outlineW, shadowOff, shadowBlur, glowRadius, lineHeight, lineGapPx, reflectShift, reflectBlur, letterSp };
}

// Safe color parsing (SSR-friendly)
const toRGB = (c) => {
  try {
    if (typeof document === 'undefined') return { r:255, g:255, b:255 };
    const ctx = document.createElement('canvas').getContext('2d');
    ctx.fillStyle = c;
    const m = ctx.fillStyle.match(/\d+/g);
    if (!m) return { r:255,g:255,b:255 };
    const [r,g,b] = m?.map(Number);
    return { r, g, b };
  } catch { return { r:255, g:255, b:255 }; }
};
const relLum = ({r,g,b}) => {
  const norm = (v)=>{ v/=255; return v<=0.03928? v/12.92 : Math.pow((v+0.055)/1.055, 2.4); };
  const R=norm(r), G=norm(g), B=norm(b);
  return 0.2126*R + 0.7152*G + 0.0722*B;
};
const contrast = (c1, c2) => {
  const L1 = relLum(toRGB(c1)) + 0.05;
  const L2 = relLum(toRGB(c2)) + 0.05;
  return L1 > L2 ? L1/L2 : L2/L1;
};
function pickContours(textColor, bg = '#00000000') {
  const isLight = relLum(toRGB(textColor)) > 0.5;
  let stroke = isLight ? 'rgba(0,0,0,0.9)' : 'rgba(255,255,255,0.95)';
  let shadow = 'rgba(0,0,0,0.35)';
  if (contrast(textColor, bg) < 3) stroke = isLight ? 'rgba(0,0,0,1)' : 'rgba(255,255,255,1)';
  return { stroke, shadow };
}

/* ==========================
 * Variant tune presets + precision
 * ========================== */
const VARIANT_TUNES = {
  neonGlow:        { glowK: 0.22, blurK: 0.14, shadowK: 0.06 },
  yellowOutline:   { strokeK: 0.030, shadowK: 0.07 },
  redCartoon:      { outlineK: 0.050, shadowK: 0.10, blurK: 0.14 },
  chromeBevel:     { shadowK: 0.07, blurK: 0.10 },
  grey3D:          { shadowK: 0.08, blurK: 0.12 },
  glassy:          { strokeK: 0.020, shadowK: 0.08, blurK: 0.12 },
  gradientOutline: { strokeK: 0.040, shadowK: 0.08 },
  bubbleGum:       { outlineK: 0.048, blurK: 0.12 },
  halftoneComic:   { outlineK: 0.042, shadowK: 0.10 },
};

function normalizePrecision(p) {
  if (p == null) return 1;
  if (typeof p === 'number') return p;
  const map = { low: 0.85, medium: 1, high: 1.25 };
  return map[String(p).toLowerCase()] ?? 1;
}
const mergeTunes = (a={},b={}) => ({ ...a, ...b });

function resolveEffectiveTune({ baseVariant, sizePx, lines, scaleMode, precision, tune }) {
  const preset = VARIANT_TUNES[baseVariant] || {};
  const dens = normalizePrecision(precision);

  if (scaleMode === 'custom') {
    return mergeTunes({ density: dens }, mergeTunes(preset, tune || {}));
  }

  const s = parseFloat(sizePx);
  const sizeBoost = s < 80 ? 0.92 : s < 120 ? 1.00 : s < 180 ? 1.08 : 1.15;
  const lineBoost = lines > 1 ? 1.08 : 1.00;
  return mergeTunes({ density: sizeBoost * lineBoost }, preset);
}

/* ==========================
 * Component
 * ========================== */
export default function ThreeDText({
  text = 'Lakay Yvox',
  size = 140,
  variant = 'grey3D', // supports "base" or "base:font"
  colors = {},
  reflect = true,
  className = '',
  style = {},
  position = null,
  zIndex = 999,
  placement = 'inline',
  offset = { x: 0, y: 0 },
  pos = null,
  textAlign = 'center',
  shop = true,             // render gate

  // Contrôles lisibilité/échelle
  scaleMode = 'auto',      // "auto" | "custom"
  precision = 'medium',    // "low"|"medium"|"high"|number
  tune = {},               // réglages fins optionnels

  // 🆕 Background control
  background = { type: 'none', color: 'transparent', opacity: 1.0, radius: '0px', padding: undefined },
}) {
  const variantStr = String(variant || 'grey3D');
  const [baseVariant, fontVariant] = variantStr.split(':');

  const sz = resolveSizeVars(size);
  const sBaseNum = parseFloat(sz.base); // ex: "140px" -> 140
  const lines = countLines(text);
  const effTune = resolveEffectiveTune({
    baseVariant,
    sizePx: sBaseNum,
    lines,
    scaleMode,
    precision,
    tune
  });

  const preset = PRESET_COLORS[baseVariant] || {};
  const defaultStack = "FallbackDisplay, system-ui, -apple-system, 'Segoe UI', Roboto, 'Helvetica Neue', Arial";
  const typeface = TYPEFACE_MAP[fontVariant] || TYPEFACE_MAP[baseVariant] || defaultStack;

  const alias = parsePos(pos);
  const effPlacement = pos ? alias.placement : placement;
  const effOffset = pos ? alias.offset : offset;

  // 🆕 Background color resolve
  let bgColor = 'transparent';
  if (background?.type === 'color') {
    bgColor = background.color || 'transparent';
  } else if (background?.type === 'transparent') {
    const alpha = typeof background.opacity === 'number'
      ? clampNum(background.opacity, 0, 1)
      : 0.5;
    // couleur de base (noire par défaut), peut être fournie
    const base = background.color || '#000';
    // transform base hex/rgb en rgba avec alpha (simplifié : on applique alpha via overlay)
    // Ici on laisse le navigateur interpréter base; la transparence vient d'alpha
    // Si base est déjà rgba(a), on se contente de l'utiliser tel quel.
    bgColor = base.startsWith('rgba') ? base : `${base}80`; // fallback léger
    // Mieux: on force rgba par un wrapper inline:
    // On laisse bgColor = 'transparent' et on applique via style.backgroundColor plus bas.
  }

  // Couleurs + contraste auto
  const textColor = colors.text ?? preset.text ?? '#ffffff';
  const bgApprox  = colors.bg ?? (background?.type === 'color' ? (background.color || 'transparent') : 'transparent');
  const { stroke: autoStroke, shadow: autoShadow } = pickContours(textColor, bgApprox);

  // Proportions à partir du size
  const m = computeTextMetrics(sBaseNum, lines, effTune);

  const vars = {
    '--td-text': textColor,
    '--td-stroke': colors.stroke ?? preset.stroke ?? autoStroke,
    '--td-outline': colors.outline ?? preset.outline ?? autoStroke,
    '--td-shadow': colors.shadow ?? preset.shadow ?? autoShadow,
    '--td-reflect': colors.reflection ?? preset.reflection ?? 'rgba(0,0,0,0.5)',
    '--td-grad-start': colors.gradientStart ?? preset.gradientStart ?? '#ffffff',
    '--td-grad-end': colors.gradientEnd ?? preset.gradientEnd ?? '#cfcfcf',
    '--td-glow': colors.glow ?? preset.glow ?? 'rgba(255,255,255,0.9)',

    '--td-fz': sz.base,
    '--td-fz-sm': sz.sm ?? sz.base,
    '--td-fz-md': sz.md ?? sz.base,
    '--td-fz-lg': sz.lg ?? sz.base,

    '--td-font': typeface,
    '--td-align': resolveTextAlign(textAlign),

    // variables proportionnelles
    '--td-stroke-w': `${m.strokeW}px`,
    '--td-outline-w': `${m.outlineW}px`,
    '--td-shadow-off': `${m.shadowOff}px`,
    '--td-shadow-blur': `${m.shadowBlur}px`,
    '--td-glow-radius': `${m.glowRadius}px`,
    '--td-line-height': m.lineHeight,
    '--td-line-gap': `${m.lineGapPx}px`,
    '--td-reflect-shift': `${m.reflectShift}px`,
    '--td-reflect-blur': `${m.reflectBlur}px`,
    '--td-letter-spacing': `${m.letterSp}px`,
  };

  const placementStyle = resolvePlacement(effPlacement, effOffset);

  // Background style final (transparent 0..1 géré ici)
  let backgroundColor = undefined;
  if (background?.type === 'color') {
    backgroundColor = background.color || undefined;
  } else if (background?.type === 'transparent') {
    const alpha = typeof background.opacity === 'number'
      ? clampNum(background.opacity, 0, 1)
      : 0.5;
    const base = background.color || '#000';
    // On convertit une couleur quelconque en rgba(alpha) via CSS filter? plus simple: on passe une RGBA approximée
    // Petite astuce: si base est #rrggbb on calcule une rgba rapide ici:
    const rgba = hexToRgba(base, alpha);
    backgroundColor = rgba || `rgba(0,0,0,${alpha})`;
  } else if (background?.type === 'glass') {
    const alpha = typeof background.opacity === 'number'
      ? clampNum(background.opacity, 0, 1)
      : 0.10;
    const rgba = hexToRgba(background.color || '#ffffff', alpha);
    backgroundColor = rgba || `rgba(255,255,255,${alpha})`;
  } // 'none' => undefined

  // 🆕 Carte "glassmorphism" responsive : flou, bordure fine, ombre douce + accent latéral
  const isGlass = background?.type === 'glass';
  const glassStyle = isGlass ? {
    backdropFilter: `blur(${background.blur ?? '12px'})`,
    WebkitBackdropFilter: `blur(${background.blur ?? '12px'})`,
    border: `1px solid ${background.borderColor || 'rgba(255,255,255,0.18)'}`,
    boxShadow: background.boxShadow || '0 8px 32px rgba(0,0,0,0.28)',
    borderLeft: background.accent ? `4px solid ${background.accent}` : undefined,
    maxWidth: background.maxWidth || '92vw',
    boxSizing: 'border-box',
    overflowWrap: 'anywhere',
    wordBreak: 'break-word',
  } : {};

  const wrapperStyle = {
    ...(effPlacement !== 'inline'
      ? placementStyle
      : { position: (position && position.position) || undefined, ...position }),
    zIndex,
    backgroundColor,                       // 🆕 fond géré ici
    borderRadius: background?.radius ?? (isGlass ? 'clamp(8px, 1.5vw, 16px)' : undefined),
    padding: background?.padding ?? (isGlass ? 'clamp(6px, 1.8vw, 16px) clamp(10px, 3vw, 22px)' : undefined),
    ...glassStyle,
    ...style,
  };

  const aria = typeof text === 'string'
    ? text.replaceAll('.;', ' ').trim()
    : undefined;

  const content = renderWithLineBreaks(text);

  // Alléger les effets sur très petits écrans
  const hideHeavyFx = sBaseNum <= 64;
  const shouldReflect = reflect && !hideHeavyFx;

  return (
    shop && (
      <div
        className={`td-wrap ${isGlass ? 'td-glass' : ''} ${className}`}
        style={varsToStyle(vars, wrapperStyle)}
        data-variant={baseVariant}
        data-font={fontVariant || ''}
        aria-label={aria}
        role="img"
      >
        <span className="td-text" aria-hidden={false}>{content}</span>
        {shouldReflect && <span className="td-reflection" aria-hidden>{content}</span>}
      </div>
    )
  );
}

/* ==========================
 * Inline style merger
 * ========================== */
function varsToStyle(vars, base) {
  const style = { ...(base || {}) };
  for (const k in vars) style[k] = vars[k];
  return style;
}

/* ==========================
 * Small util: hex -> rgba
 * ========================== */
function hexToRgba(hex, alpha = 1) {
  if (typeof hex !== 'string') return null;
  const m = hex.trim().toLowerCase();
  // support #rgb, #rgba, #rrggbb, #rrggbbaa
  if (!m.startsWith('#')) return null;
  const h = m.slice(1);
  if (h?.length === 3) {
    const r = parseInt(h[0] + h[0], 16);
    const g = parseInt(h[1] + h[1], 16);
    const b = parseInt(h[2] + h[2], 16);
    return `rgba(${r},${g},${b},${alpha})`;
  }
  if (h?.length === 6 || h?.length === 8) {
    const r = parseInt(h.slice(0,2), 16);
    const g = parseInt(h.slice(2,4), 16);
    const b = parseInt(h.slice(4,6), 16);
    if (h?.length === 8) {
      const aHex = parseInt(h.slice(6,8), 16);
      const a = clampNum(aHex / 255, 0, 1);
      return `rgba(${r},${g},${b},${a * alpha})`;
    }
    return `rgba(${r},${g},${b},${alpha})`;
  }
  return null;
}
